import { Fragment, type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Checkbox from "~/common/components/Checkbox";
import type { QuoteLineMO } from "~/features/orders/common/components/v2/models";
import type { RenderCboxFn } from "~/features/orders/common/components/v2/QuoteTable/types";
import type { QuoteSelectionStateProps } from "~/features/orders/common/components/v2/QuoteTable/useQuoteDetailsSelectionState";
import { groupByMainQuoteLine } from "~/features/orders/common/components/v2/QuoteTable/utils";
import { QuoteGrid, QuoteHeader, HeaderCell, HeaderSubtitle } from "./styled";
import { SimpleQuoteRow } from "./SimpleQuoteRow";
import { AdvancedQuoteRow } from "./AdvancedQuoteRow";

// ============================================================================
// Types
// ============================================================================

interface QuoteTableProps extends Partial<QuoteSelectionStateProps> {
  quoteLines: QuoteLineMO[];
  className?: string;
  showCheckboxes: boolean;
  classes?: {
    headerCheckbox?: string;
    lineItemCheckbox?: string;
  };
}

// ============================================================================
// Component
// ============================================================================

export const QuoteDetailsTable = ({
  quoteLines,
  className,
  classes: { headerCheckbox: headerCheckboxClass, lineItemCheckbox: lineItemCheckboxClass } = {},
  ...rest
}: QuoteTableProps) => {
  const { t } = useTranslation();
  const groupedLines = useMemo(() => groupByMainQuoteLine(quoteLines), [quoteLines]);

  // Checkbox rendering
  let renderQuoteLineCheckbox: RenderCboxFn = () => null;
  let headerCheckbox: ReactNode = null;

  if (rest.showCheckboxes) {
    const { isAllSelected, isSomeSelected, isAllDisabled, selectedLineNos, toggleAllLines, toggleLine } =
      rest as QuoteSelectionStateProps;

    if (rest.toggleLine) {
      renderQuoteLineCheckbox = (quoteLine: QuoteLineMO) => (
        <Checkbox
          className={lineItemCheckboxClass}
          disabled={quoteLine.disabled}
          checked={selectedLineNos[quoteLine.lineNo] ?? false}
          onCheck={() => toggleLine(quoteLine.lineNo)}
        />
      );
    }

    if (rest.toggleAllLines) {
      headerCheckbox = (
        <Checkbox
          className={headerCheckboxClass}
          disabled={isAllDisabled}
          checked={isSomeSelected}
          indeterminate={isSomeSelected && !isAllSelected}
          onCheck={toggleAllLines}
        />
      );
    }
  }

  const header = (suffix: string) => t(`quoteDetailsPage.table.header.${suffix}`);

  return (
    <QuoteGrid role="grid" aria-label={t("quoteDetailsPage.table.ariaLabel")} className={className}>
      {/* Header Row */}
      <QuoteHeader role="row">
        <HeaderCell role="columnheader">{header("lineNo")}</HeaderCell>
        <HeaderCell role="columnheader">{header("product")}</HeaderCell>
        <HeaderCell role="columnheader">
          {header("quantityConfirmed")}
          <HeaderSubtitle>{t("quoteListPage.table.header.requestedQuantity")}</HeaderSubtitle>
        </HeaderCell>
        <HeaderCell role="columnheader">
          {t("quoteDetailsPage.summary.packagingCode")}
          <HeaderSubtitle>{t("cartPage.table.header.packaging.subtitle")}</HeaderSubtitle>
        </HeaderCell>
        <HeaderCell role="columnheader">
          {t("quoteListPage.table.header.estimatedDeliveryDate")}
          <HeaderSubtitle>{t("cartPage.table.header.delivery.title")}</HeaderSubtitle>
        </HeaderCell>
        <HeaderCell role="columnheader">
          {t("order.report.header.weight")}
          <HeaderSubtitle>{t("orderDetailsPage.labels.weightUnit")}</HeaderSubtitle>
        </HeaderCell>
        <HeaderCell role="columnheader">
          {t("quoteListPage.table.header.netPrice")}
          <HeaderSubtitle>{t("wishlistPage.table.header.priceUnit")}</HeaderSubtitle>
        </HeaderCell>
        <HeaderCell role="columnheader">{header("total")}</HeaderCell>
        <HeaderCell role="columnheader">{t("quoteListPage.table.header.lineItemStatus")}</HeaderCell>
        <HeaderCell role="columnheader" isCheckbox>
          {headerCheckbox}
        </HeaderCell>
      </QuoteHeader>

      {/* Quote Groups */}
      {groupedLines.map((group) => (
        <Fragment key={group.mainQuoteLine.lineNo}>
          {group.alternativeQuoteLines.length === 0 ? (
            <SimpleQuoteRow quoteLine={group.mainQuoteLine} renderCheckbox={renderQuoteLineCheckbox} />
          ) : (
            <AdvancedQuoteRow {...group} renderCheckbox={renderQuoteLineCheckbox} />
          )}
        </Fragment>
      ))}
    </QuoteGrid>
  );
};
